import { useEffect, useState } from 'react';
import { Avatar, Box, Chip, CircularProgress, Stack, Typography } from '@mui/material';
import FmdGoodOutlinedIcon from '@mui/icons-material/FmdGoodOutlined';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import Tab from '@mui/material/Tab';
import Tooltip from '@mui/material/Tooltip';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { getUniqueInfluencerDetails } from './action';
import { resetDetails } from './ultimateDetailsSlice';
import { TOOLTIP_STYLES, getFixedValue } from '../../utils/constant';
import verifiedIcon from '../../assets/images/icons/verifiedIcon.svg'
import profileSummaryIcon from '../../assets/images/icons/profileSummaryIcon.svg'
import UltimateProfileSummary from './UltimateProfileSummary';
import UltimateSocialProfileSummary from './UltimateSocialProfileSummary';

// Detailed data can be pulled live from Influencers Club (~15s) when it isn't in our DB.
// Cycle reassuring status text under the loader so the user doesn't feel stuck.
const LOADING_MESSAGES = [
  'Fetching profile info',
  'Analyzing audience data',
  'Preparing final view',
];

const DetailsLoader = () => {
  const [messageIndex, setMessageIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setMessageIndex((prev) => (prev < LOADING_MESSAGES.length - 1 ? prev + 1 : prev));
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '60vh', gap: 2 }}>
      <CircularProgress />
      <Typography
        key={messageIndex}
        sx={{
          fontSize: 14,
          fontWeight: 500,
          color: '#726C6C',
          animation: 'fadeIn .4s ease-in-out',
          '@keyframes fadeIn': { from: { opacity: 0 }, to: { opacity: 1 } },
        }}
      >
        {LOADING_MESSAGES[messageIndex]}
      </Typography>
    </Box>
  );
};

const UltimateInfluencerDetails = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const [value, setValue] = useState('1');
  const [platformTabData, setPlatformTabData] = useState(null);
  const { influencerDetails, loading, error } = useSelector((state) => state.ultimateDetailsSlice);

  useEffect(() => {
    if (id) {
      dispatch(getUniqueInfluencerDetails(id));
    }
    return () => {
      dispatch(resetDetails());
    };
  }, [id, dispatch]);

  const handleChange = (event, newValue) => {
    setValue(newValue);
    if (newValue !== '1' && influencerDetails?.platformDetails) {
      const found = influencerDetails.platformDetails.find(p => p.handleName === newValue);
      setPlatformTabData(found || null);
    }
  };

  const getPlatformIcon = (platform) => {
    if (platform?.toLowerCase() === 'youtube') return '/youtube.svg';
    return '/instagram.png';
  };

  const categories = influencerDetails?.categories || [];
  const locationParts = [
    influencerDetails?.location_city,
    influencerDetails?.location_state,
    influencerDetails?.location_country,
    influencerDetails?.location
  ].filter(Boolean).join(', ');

  const nicheDisplay = influencerDetails?.platformDetails?.[0]?.niche_class || [];

  // Show the loader while fetching, and during the brief window before the
  // fetch kicks in (loading not yet set, no data, no error) to avoid a flash.
  if (loading || (!influencerDetails && !error)) {
    return <DetailsLoader />;
  }

  if (error || !influencerDetails) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="error">Influencer not found</Typography>
      </Box>
    );
  }

  return (
    <Stack direction="column" gap={3}>
      <Stack direction={"row"} alignItems={"center"} className='justify-content-between' spacing={3} sx={{ padding: '16px' }}>
        <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"} sx={{ minHeight: '42px' }}>
          <Typography variant="h1" className="page-title">
            View Details
          </Typography>
          <Stack
            className="backText"
            direction={"row"}
            gap={1}
            alignItems={"center"}
            sx={{ cursor: "pointer" }}
            onClick={() => navigate(-1)}
          >
            <svg
              width="16"
              height="12"
              viewBox="0 0 16 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M8.5 7.66667L5.16667 4.33333L8.5 1M4.33333 7.66667L1 4.33333L4.33333 1"
                stroke="#0077FF"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M5.1665 4.33331H10.9998C11.8839 4.33331 12.7317 4.6845 13.3569 5.30962C13.982 5.93474 14.3332 6.78259 14.3332 7.66665C14.3332 8.5507 13.982 9.39855 13.3569 10.0237C12.7317 10.6488 11.8839 11 10.9998 11H10.1665"
                stroke="#0077FF"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            <Typography variant="h5" color={"primary"}>
              Back
            </Typography>
          </Stack>
        </Stack>
      </Stack>

      <Box sx={{
        backgroundColor: '#fff',
        transition: 'all .5s ease-in-out',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <Box sx={{ border: '0px solid red', paddingX: '16px' }}>
          <Stack direction={"row"} justifyContent={"space-between"} alignItems={"center"} padding={"20px"}>
            <Stack direction={"row"} justifyContent={"space-between"} alignItems={"center"} gap={2} width={"50%"}>
              <Avatar
                sx={{ height: '94px', width: '94px' }}
                src={influencerDetails.profile_picture || '/profile1.jpg'}
              />
              <Stack direction={"column"} gap={1} sx={{ border: '0px solid red', height: '94px', width: '100%' }}>
                <Stack direction={"row"} justifyContent={"flex-start"} alignItems={"center"} gap={1}>
                  <Typography fontSize={16} fontWeight={500} color={"#000000"} textTransform={"capitalize"}>
                    {influencerDetails.full_name || influencerDetails.username}
                  </Typography>
                  {!!influencerDetails.is_verified && (
                    <img src={verifiedIcon} alt="verifiedIcon" height={16} width={16} />
                  )}
                </Stack>

                {nicheDisplay && nicheDisplay.length > 0 && (
                  <Stack direction={"row"} justifyContent={"flex-start"} gap={1} alignItems={"center"} flexWrap={"wrap"}>
                    {nicheDisplay.slice(0, 3).map((item, i) => (
                      <Tooltip key={i} title={item} arrow componentsProps={TOOLTIP_STYLES}>
                        <Chip
                          label={item?.length > 20 ? item?.slice(0, 20) + '...' : item}
                          sx={{
                            background: 'white !important',
                            color: '#726C6C !important',
                            borderRadius: '62px',
                            border: '1px solid #CCCBCB',
                            fontSize: '12px',
                            fontWeight: '500',
                            height: '32px',
                            "& .css-6od3lo-MuiChip-label ": {
                              padding: '2px 4px'
                            }
                          }}
                        />
                      </Tooltip>
                    ))}
                  </Stack>
                )}

                <Stack direction={"row"}>
                  {locationParts && <FmdGoodOutlinedIcon sx={{ fontSize: 16, color: "#726C6C" }} />}
                  <Typography fontSize={12} fontWeight={400} color={'#726C6C'}>
                    {locationParts}
                  </Typography>
                </Stack>
              </Stack>
            </Stack>

            <Stack direction={"row"} justifyContent={"flex-end"} alignItems={"center"} gap={2} width={"50%"}>
              {influencerDetails.email && (
                <div className='col-xxl-4 col-xl-4 col-lg-4 col-md-6 col-sm-4 col-4'>
                  <div className='influDetphEm'>
                    <div className='influDetphEmItem mb-3'>
                      <label>Email:</label>
                      <span>{influencerDetails.email}</span>
                    </div>
                    {influencerDetails.phone && (
                      <div className='influDetphEmItem'>
                        <label>Phone:</label>
                        <span>{influencerDetails.phone}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </Stack>
          </Stack>
        </Box>

        <Box sx={{ width: '100%', typography: 'body1' }}>
          <TabContext value={value}>
            <Box sx={{ paddingX: '16px' }}>
              <TabList
                sx={{
                  borderBottom: "0.5px solid #F0F0F0",
                  boxSizing: 'content-box',
                  "& .MuiTabs-indicator": { backgroundColor: "blue", height: 2, position: 'absolute' },
                  "& .MuiTab-root": {
                    textTransform: "none",
                    fontWeight: 500,
                    minHeight: 48,
                    color: "#726C6C",
                    justifyContent: 'flex-start',
                    maxWidth: 'revert',
                    "&.Mui-selected": { color: "blue", width: 'revert', borderBottom: "2px solid blue" },
                  },
                }}
                onChange={handleChange}
                variant="scrollable"
                scrollButtons={influencerDetails?.platformDetails?.length > 4}
                allowScrollButtonsMobile
                aria-label="scrollable force tabs example"
              >
                <Tab
                  sx={{ border: value === "1" ? "0px solid #0077FF" : "0px solid orange", fontSize: '16px', fontWeight: 400, padding: '4px 8px' }}
                  iconPosition="start"
                  icon={<img src={profileSummaryIcon} alt="profile" width={24} height={24} />}
                  label="Profile Summary"
                  value="1"
                />
                {influencerDetails?.platformDetails?.map((platform, i) => (
                  <Tab
                    key={i}
                    sx={{ border: value === platform.handleName ? "0px solid #0077FF" : "", fontSize: '16px', fontWeight: 400, padding: '4px 8px', marginLeft: '16px' }}
                    iconPosition="start"
                    icon={<img src={getPlatformIcon(platform.platformType)} alt={platform.platformType} width={24} height={24} />}
                    label={platform.handleName}
                    value={platform.handleName}
                  />
                ))}
              </TabList>
            </Box>

            <TabPanel value="1" className='p-0'>
              <UltimateProfileSummary
                details={influencerDetails?.platformDetails || []}
                influencerDetails={influencerDetails}
              />
            </TabPanel>

            <TabPanel value={platformTabData?.handleName || value} className='p-0'>
              {platformTabData && (
                <UltimateSocialProfileSummary
                  data={platformTabData}
                  influencerDetails={influencerDetails}
                />
              )}
            </TabPanel>
          </TabContext>
        </Box>
      </Box>
    </Stack>
  );
};

export default UltimateInfluencerDetails;